import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MessageSquare, ArrowLeft, Mail, ExternalLink } from "lucide-react";
import Navbar from "../components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "bg-accent/10 text-brand-accent",
  contacted: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400",
  closed: "bg-muted text-muted-foreground",
};

export default function AdminInquiries() {
  const { token } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchInquiries();
  }, [filter]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/admin/inquiries${params}`, { withCredentials: true, headers });
      setInquiries(res.data || []);
    } catch {
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/inquiries/${id}/status`, { status }, { withCredentials: true, headers });
      setInquiries((prev) => prev.map((inq) => inq.id === id ? { ...inq, status } : inq));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16 px-4 sm:px-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/admin" className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <p className="overline mb-1">Admin</p>
            <h1 className="font-cormorant text-3xl font-semibold text-foreground">Inquiries</h1>
          </div>
        </div>

        {/* Summary badges */}
        <div className="flex flex-wrap gap-3 mb-8">
          {[
            { label: "All", value: "all", count: inquiries.length },
            { label: "New", value: "new", count: inquiries.filter((i) => i.status === "new").length },
            { label: "Contacted", value: "contacted", count: inquiries.filter((i) => i.status === "contacted").length },
            { label: "Closed", value: "closed", count: inquiries.filter((i) => i.status === "closed").length },
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={`inquiry-filter-${f.value}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-manrope text-sm font-medium transition-colors ${
                filter === f.value ? "bg-brand-accent text-white" : "border border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {f.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                filter === f.value ? "bg-white/20" : "bg-muted"
              }`}>
                {f.count}
              </span>
            </button>
          ))}
        </div>

        {/* Inquiries */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-xl">
            <MessageSquare size={40} className="text-muted-foreground mx-auto mb-4" />
            <p className="font-cormorant text-2xl text-foreground mb-2">No inquiries yet</p>
            <p className="font-manrope text-sm text-muted-foreground">Inquiries from your "Hire Elizabeth" page will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inq, i) => (
              <motion.div
                key={inq.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.35 }}
                className="bg-card border border-border rounded-xl overflow-hidden"
                data-testid={`inquiry-row-${inq.id}`}
              >
                {/* Summary row */}
                <div
                  className="flex items-center gap-4 p-5 cursor-pointer hover:bg-muted/20 transition-colors"
                  onClick={() => setExpanded(expanded === inq.id ? null : inq.id)}
                >
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center font-cormorant text-sm font-semibold text-foreground shrink-0">
                    {inq.name[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-manrope text-sm font-semibold text-foreground">{inq.name}</p>
                      {inq.company && <p className="font-manrope text-xs text-muted-foreground">· {inq.company}</p>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="font-manrope text-xs text-muted-foreground">{inq.service_type}</span>
                      {inq.budget && (
                        <span className="font-manrope text-xs text-muted-foreground">· Budget: {inq.budget}</span>
                      )}
                      <span className="font-manrope text-xs text-muted-foreground">{formatDate(inq.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`font-manrope text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[inq.status] || STATUS_STYLES.new}`}>
                      {inq.status}
                    </span>
                  </div>
                </div>

                {/* Expanded detail */}
                {expanded === inq.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-border px-5 py-4"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="font-manrope text-xs font-semibold text-muted-foreground mb-1">Email</p>
                        <a href={`mailto:${inq.email}`} className="font-manrope text-sm text-brand-accent hover:text-brand-accent-hover flex items-center gap-1.5 transition-colors">
                          <Mail size={13} /> {inq.email}
                        </a>
                      </div>
                      {inq.company && (
                        <div>
                          <p className="font-manrope text-xs font-semibold text-muted-foreground mb-1">Company</p>
                          <p className="font-manrope text-sm text-foreground">{inq.company}</p>
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <p className="font-manrope text-xs font-semibold text-muted-foreground mb-1">Project Description</p>
                      <p className="font-manrope text-sm text-foreground leading-relaxed bg-muted/50 rounded-lg p-3">{inq.description}</p>
                    </div>

                    {/* Status Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-manrope text-xs font-semibold text-foreground">Update status:</p>
                      {["new", "contacted", "closed"].map((s) => (
                        <button
                          key={s}
                          onClick={() => updateStatus(inq.id, s)}
                          data-testid={`status-${s}-${inq.id}`}
                          className={`px-3 py-1.5 rounded-full font-manrope text-xs font-medium transition-colors ${
                            inq.status === s
                              ? "bg-brand-accent text-white"
                              : "border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                          }`}
                        >
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </button>
                      ))}
                      <a
                        href={`mailto:${inq.email}?subject=Re: Your writing inquiry&body=Hi ${inq.name},%0A%0AThank you for reaching out...`}
                        className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-accent text-white font-manrope text-xs font-medium hover:bg-brand-accent-hover transition-colors"
                        data-testid={`reply-inquiry-${inq.id}`}
                      >
                        <Mail size={13} /> Reply via email
                      </a>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
